import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, CircularProgress, TextField } from '@/shared/ui';
import { InventoryItem, listInventoryItems } from '@/modules/inventory/api/inventory.api';

interface InventoryItemPickerProps {
  value: InventoryItem | null;
  onChange: (item: InventoryItem | null) => void;
  disabled?: boolean;
  label?: string;
  error?: boolean;
  helperText?: string;
  /** When set, the menu uses this list and does not fetch the catalog. */
  items?: InventoryItem[];
  loading?: boolean;
  /**
   * Flush control for EditableTableCell: placeholder only, no floating label.
   * Avoids stacking OutlinedInput chrome on top of the cell focus ring.
   */
  embedded?: boolean;
}

const InventoryItemPicker: React.FC<InventoryItemPickerProps> = ({
  value,
  onChange,
  disabled = false,
  label,
  error = false,
  helperText,
  embedded = false,
  items,
  loading: loadingProp = false,
}) => {
  const { t } = useTranslation('inventory');
  const [fetched, setFetched] = useState<InventoryItem[]>([]);
  const [fetching, setFetching] = useState(false);
  const resolvedLabel = label ?? t('picker.label');
  const usesProvidedItems = items !== undefined;

  useEffect(() => {
    if (usesProvidedItems) return;
    const load = async () => {
      setFetching(true);
      try {
        const rows = await listInventoryItems();
        setFetched(rows);
      } catch (err) {
        console.error('Failed to load inventory items:', err);
      } finally {
        setFetching(false);
      }
    };
    void load();
  }, [usesProvidedItems]);

  const options = usesProvidedItems ? items : fetched;
  const loading = usesProvidedItems ? loadingProp : fetching;

  return (
    <Autocomplete
      fullWidth
      size="small"
      disableClearable={embedded}
      options={options}
      value={value}
      onChange={(_e, newValue) => onChange(newValue)}
      disabled={disabled}
      loading={loading}
      getOptionLabel={(option) => option.itemName}
      isOptionEqualToValue={(option, val) => option._id === val._id}
      // Table cell owns the focus ring; keep popup/clear affordance quiet until hover.
      slotProps={
        embedded
          ? {
              clearIndicator: { size: 'small' },
              popupIndicator: { size: 'small' },
            }
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={embedded ? undefined : resolvedLabel}
          placeholder={embedded ? resolvedLabel : undefined}
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            htmlInput: {
              ...params.inputProps,
              'aria-label': resolvedLabel,
            },
          }}
        />
      )}
    />
  );
};

export default InventoryItemPicker;
